import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { QueryClient } from "@project/ui";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { formatDateTime, formatShort } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import {
  deleteExpense,
  fetchDashboardSummary,
  fetchExpense,
  fetchExpenseCategories,
  fetchHouseMembers,
  openStorageDocumentInNewTab,
  homeExpenseQueryKeys,
  markExpensePaid,
  markExpenseUnpaid,
  patchExpense,
} from "@apps/dashboard/lib/home-expense-api";
import { minorToMajorInputValue, parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { expenseEffectiveMinor } from "@apps/dashboard/lib/expense-effective-amount";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { authClient } from "@apps/dashboard/lib/auth-client";
import {
  PAID_BY_JOINT_SELECT_VALUE,
  paidByPayloadFromSelectValue,
  paidBySelectValueFromExpense,
} from "@apps/dashboard/lib/paid-by-select";
import type { Locale } from "@project/i18n";
import type { ExpensePaymentMethod } from "@apps/dashboard/lib/home-expense-types";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ExpenseSplitBreakdownCard } from "@apps/dashboard/components/expense/ExpenseSplitBreakdownCard";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { FilePondStorageUpload } from "@apps/dashboard/components/FilePondStorageUpload";
import {
  splitMinorByShareBps,
  storedExpenseSplitModeDisplay,
} from "@apps/dashboard/lib/expense-splits";

function labelForPaymentMethod(
  m: ExpensePaymentMethod | null | undefined,
  t: (key: string) => string,
): string {
  if (!m) return t("dashboardUi.expenses.detail.notApplicable");
  if (m === "card") return t("dashboardUi.expenses.form.paymentMethodCard");
  if (m === "cash") return t("dashboardUi.expenses.form.paymentMethodCash");
  return t("dashboardUi.expenses.form.paymentMethodAccount");
}

function invalidateExpenseQueries(
  qc: QueryClient,
  houseId: string,
  expenseId: string,
  locale: Locale,
): void {
  void qc.invalidateQueries({ queryKey: ["houses", houseId, "expenses"] });
  void qc.invalidateQueries({
    queryKey: homeExpenseQueryKeys.expense(locale, houseId, expenseId),
  });
}

export default function ExpenseDetailPage(): JSX.Element {
  const { houseId, expenseId } = useParams<{ houseId: string; expenseId: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useSyncHouseFromParams(houseId);

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [amountInput, setAmountInput] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [occurredOn, setOccurredOn] = useState("");
  const [categoryId, setCategoryId] = useState("");
  /** Valor del `<select>` (incluye `__joint__` para cuenta conjunta). */
  const [paidBySelect, setPaidBySelect] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentMethodDraft, setPaymentMethodDraft] = useState<"" | ExpensePaymentMethod>("");
  const [receiptStorageKey, setReceiptStorageKey] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const expenseQuery = useQuery({
    queryKey:
      houseId && expenseId
        ? homeExpenseQueryKeys.expense(locale, houseId, expenseId)
        : ["expense", "noop"],
    queryFn: () => {
      if (!houseId || !expenseId) throw new Error("missing");
      return fetchExpense(locale, houseId, expenseId);
    },
    enabled: Boolean(houseId && expenseId),
  });

  const categoriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.categories(locale, houseId) : ["cat", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchExpenseCategories(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const membersQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.houseMembers(locale, houseId) : ["mem", "x"],
    queryFn: () => {
      if (!houseId) throw new Error("no house");
      return fetchHouseMembers(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const e = expenseQuery.data;

  const expenseMonthYm =
    e?.occurredOn != null && typeof e.occurredOn === "string" && e.occurredOn.length >= 7
      ? e.occurredOn.slice(0, 7)
      : "";
  const monthSummaryQuery = useQuery({
    queryKey:
      houseId && /^\d{4}-\d{2}$/.test(expenseMonthYm)
        ? homeExpenseQueryKeys.dashboardSummary(locale, houseId, expenseMonthYm)
        : ["expense-detail-dash", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing");
      return fetchDashboardSummary(locale, houseId, expenseMonthYm);
    },
    enabled: Boolean(houseId && e && /^\d{4}-\d{2}$/.test(expenseMonthYm)),
  });

  const { data: sessionData } = authClient.useSession();
  const currentUserId =
    sessionData?.user &&
    typeof sessionData.user === "object" &&
    "id" in sessionData.user &&
    typeof (sessionData.user as { id: unknown }).id === "string"
      ? (sessionData.user as { id: string }).id
      : undefined;

  useEffect(() => {
    if (!e || editing) return;
    setTitle(e.title);
    setAmountInput(minorToMajorInputValue(e.amountMinor));
    setCurrency(e.currency);
    setOccurredOn(e.occurredOn);
    setCategoryId(e.categoryId ?? "");
    setPaidBySelect(paidBySelectValueFromExpense(e));
    setNotes(e.notes ?? "");
    setPaymentMethodDraft((e.paymentMethod ?? "") as "" | ExpensePaymentMethod);
    setReceiptStorageKey(e.receiptStorageKey ?? null);
  }, [e, editing]);

  const memberNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of membersQuery.data ?? []) {
      m.set(row.userId, row.user.name);
    }
    return m;
  }, [membersQuery.data]);

  const splitRows = useMemo(() => {
    if (!e) return [];
    const splits = e.splits ?? [];
    if (splits.length === 0) return [];
    return splitMinorByShareBps(expenseEffectiveMinor(e), splits, (uid) => memberNames.get(uid) ?? uid);
  }, [e, memberNames]);

  const splitModeDisplay = useMemo(() => {
    if (!e) return undefined;
    return storedExpenseSplitModeDisplay(
      membersQuery.data ?? [],
      e.splits ?? [],
      t,
    );
  }, [e, membersQuery.data, t]);

  const markPaidMutation = useMutation({
    mutationFn: () => {
      if (!houseId || !expenseId) throw new Error("missing");
      return markExpensePaid(locale, houseId, expenseId);
    },
    onSuccess: (detail) => {
      if (!houseId || !expenseId) return;
      invalidateExpenseQueries(queryClient, houseId, expenseId, locale);
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "dashboard"] });
      void navigate(dashboardPaths.houseExpensePaidFeedback(houseId, expenseId), {
        state: { expense: detail },
      });
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setFormError(msg);
    },
  });

  const markUnpaidMutation = useMutation({
    mutationFn: () => {
      if (!houseId || !expenseId) throw new Error("missing");
      return markExpenseUnpaid(locale, houseId, expenseId);
    },
    onSuccess: () => {
      if (!houseId || !expenseId) return;
      invalidateExpenseQueries(queryClient, houseId, expenseId, locale);
      void expenseQuery.refetch();
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setFormError(msg);
    },
  });

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!houseId || !expenseId) throw new Error("missing");
      const amountMinor = parseAmountToMinor(amountInput);
      if (amountMinor === null) throw new Error("invalid_amount");
      const ccy = currency.trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(ccy)) throw new Error("invalid_currency");
      const payer =
        currentUserId != null
          ? paidByPayloadFromSelectValue(paidBySelect, {
              explicitDefaultPayerUserId: currentUserId,
            })
          : paidByPayloadFromSelectValue(paidBySelect);
      return patchExpense(locale, houseId, expenseId, {
        title: title.trim(),
        amountMinor,
        currency: ccy,
        occurredOn,
        categoryId: categoryId || null,
        ...payer,
        notes: notes.trim() || null,
        paymentMethod: paymentMethodDraft === "" ? null : paymentMethodDraft,
        receiptStorageKey,
      });
    },
    onSuccess: () => {
      if (!houseId || !expenseId) return;
      setEditing(false);
      setFormError(null);
      invalidateExpenseQueries(queryClient, houseId, expenseId, locale);
      void expenseQuery.refetch();
    },
    onError: (err: unknown) => {
      if (err instanceof Error && err.message === "invalid_amount") {
        setFormError(t("dashboardUi.expenses.form.invalidAmount"));
        return;
      }
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setFormError(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!houseId || !expenseId) throw new Error("missing");
      return deleteExpense(locale, houseId, expenseId);
    },
    onSuccess: () => {
      if (!houseId) return;
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "expenses"] });
      void navigate(dashboardPaths.houseExpenses(houseId));
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setFormError(msg);
    },
  });

  if (!houseId || !expenseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  if (expenseQuery.isLoading) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <LoadingState />
      </div>
    );
  }

  if (expenseQuery.isError || !e) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseExpenses(houseId)}>
          {t("dashboardUi.expenses.backToJournal")}
        </DashboardBackLink>
        <ErrorState onRetry={() => void expenseQuery.refetch()} />
      </div>
    );
  }

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.houseExpenses(houseId)}>
        {t("dashboardUi.expenses.backToJournal")}
      </DashboardBackLink>
      <div>
        <h1 className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.pages.expenseDetail")}</h1>
        <p className="mt-1 text-label-md text-on-surface-variant">{formatShort(e.occurredOn)}</p>
      </div>

      {formError ? <p className="mt-4 text-body-md text-error">{formError}</p> : null}

      {monthSummaryQuery.data &&
      monthSummaryQuery.data.plannedBudgetMinor <= 0 &&
      (monthSummaryQuery.data.monthlyBudgetCapMinor ?? 0) <= 0 ? (
        <div className="mt-6 rounded-2xl border border-warning-500/30 bg-warning-50 p-4 dark:bg-warning-950/25">
          <p className="font-headline text-sm font-bold text-warning-800 dark:text-warning-200">
            {t("dashboardUi.expenses.detail.budgetSetupBannerTitle")}
          </p>
          <p className="mt-2 text-body-sm text-warning-900/90 dark:text-warning-100/85">
            {t("dashboardUi.expenses.detail.budgetSetupBannerBody")}
          </p>
          <Button asChild className="mt-3" variant="outline" size="sm">
            <Link
              to={`${dashboardPaths.houseBudget(houseId)}?ym=${encodeURIComponent(expenseMonthYm)}`}
            >
              {t("dashboardUi.expenses.detail.budgetSetupBannerCta")}
            </Link>
          </Button>
        </div>
      ) : null}

      {!editing ? (
        <>
          <div className="mt-8 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15">
            <p className="font-headline text-2xl font-bold text-on-surface">{e.title}</p>
            <p className="mt-2 font-headline text-xl text-primary">
              {formatMinorAsCurrency(e.amountMinor, e.currency, locale)}
            </p>
            {e.accrualPreviewMinor != null ? (
              <p className="mt-1 text-body-sm text-on-surface-variant">
                {t("dashboardUi.budget.proratedReserveHint")}:{" "}
                {formatMinorAsCurrency(e.accrualPreviewMinor, e.currency, locale)}
                {e.periodTotalMinor != null
                  ? ` · ${t("dashboardUi.budget.proratedTotalBadge", {
                      amount: formatMinorAsCurrency(e.periodTotalMinor, e.currency, locale),
                    })}`
                  : ""}
              </p>
            ) : null}
            <dl className="mt-6 space-y-2 text-body-md text-on-surface-variant">
              {e.seriesId ? (
                <div className="mb-4">
                  <Link
                    className="text-label-md font-medium text-primary underline"
                    to={dashboardPaths.houseExpenseSeries(houseId, e.seriesId)}
                  >
                    {t("dashboardUi.expenses.detail.seriesLink")}
                  </Link>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <dt className="font-medium text-on-surface">{t("dashboardUi.expenses.category")}:</dt>
                <dd>{e.category?.name ?? t("dashboardUi.expenses.detail.notApplicable")}</dd>
              </div>
              <div className="flex flex-wrap gap-2">
                <dt className="font-medium text-on-surface">{t("dashboardUi.expenses.paidBy")}:</dt>
                <dd>
                  {e.paidFromJointAccount === true
                    ? t("dashboardUi.expenses.form.paidByJointOption")
                    : (e.paidBy?.name ?? t("dashboardUi.expenses.detail.notApplicable"))}
                </dd>
              </div>
              <div className="flex flex-wrap gap-2">
                <dt className="font-medium text-on-surface">{t("dashboardUi.expenses.detail.scopeLabel")}:</dt>
                <dd>
                  {e.contributionScope === "shared"
                    ? t("dashboardUi.expenses.scopeShared")
                    : e.contributionScope === "personal"
                      ? t("dashboardUi.expenses.scopePersonal")
                      : t("dashboardUi.expenses.detail.notApplicable")}
                </dd>
              </div>
              <div className="flex flex-wrap gap-2">
                <dt className="font-medium text-on-surface">{t("dashboardUi.expenses.detail.paymentMethod")}:</dt>
                <dd>{labelForPaymentMethod(e.paymentMethod, t)}</dd>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <dt className="font-medium text-on-surface">{t("dashboardUi.expenses.detail.receiptLabel")}:</dt>
                <dd>
                  {e.receiptStorageKey ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        void openStorageDocumentInNewTab(locale, e.receiptStorageKey!);
                      }}
                    >
                      {t("dashboardUi.expenses.detail.receiptView")}
                    </Button>
                  ) : (
                    t("dashboardUi.expenses.detail.receiptNotAttached")
                  )}
                </dd>
              </div>
              <div className="flex flex-wrap gap-2">
                <dt className="font-medium text-on-surface">{t("dashboardUi.expenses.paid")}:</dt>
                <dd>{e.paidAt ? t("dashboardUi.expenses.paid") : t("dashboardUi.expenses.unpaid")}</dd>
              </div>
              {e.paidAt ? (
                <div className="flex flex-wrap gap-2">
                  <dt className="font-medium text-on-surface">{t("dashboardUi.expenses.detail.paidAtLabel")}:</dt>
                  <dd>{formatDateTime(e.paidAt)}</dd>
                </div>
              ) : null}
            </dl>
            {e.notes ? (
              <div className="mt-6 border-t border-outline-variant/15 pt-4">
                <h2 className="font-headline text-sm font-bold text-on-surface">{t("dashboardUi.expenses.detail.notesTitle")}</h2>
                <p className="mt-2 whitespace-pre-wrap font-body text-body-md text-on-surface-variant">{e.notes}</p>
              </div>
            ) : null}
            {splitRows.length > 0 ? (
              <div className="mt-6 border-t border-outline-variant/15 pt-4">
                <ExpenseSplitBreakdownCard
                  title={t("dashboardUi.expenses.detail.splitsTitle")}
                  subtitle={t("dashboardUi.expenses.detail.splitsSubtitle")}
                  rows={splitRows}
                  currency={e.currency}
                  locale={locale}
                  modeLabel={splitModeDisplay?.label}
                  modeLabelTone={splitModeDisplay?.tone}
                />
              </div>
            ) : null}
          </div>

          <div className="mt-6 flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => { setFormError(null); setEditing(true); }}>
                {t("dashboardUi.expenses.detail.edit")}
              </Button>
              {e.paidAt ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={markUnpaidMutation.isPending}
                  onClick={() => {
                    setFormError(null);
                    markUnpaidMutation.mutate();
                  }}
                >
                  {t("dashboardUi.expenses.detail.markUnpaid")}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={markPaidMutation.isPending}
                  onClick={() => {
                    setFormError(null);
                    markPaidMutation.mutate();
                  }}
                >
                  {t("dashboardUi.expenses.detail.markPaid")}
                </Button>
              )}
              <Button
                type="button"
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (window.confirm(t("dashboardUi.expenses.detail.deleteConfirm"))) {
                    setFormError(null);
                    deleteMutation.mutate();
                  }
                }}
              >
                {t("dashboardUi.expenses.detail.delete")}
              </Button>
            </div>
            <div className="flex w-full justify-end sm:w-auto sm:shrink-0">
              <Button asChild variant="default">
                <Link to={dashboardPaths.houseExpenseNew(houseId)}>{t("dashboardUi.pages.expenseNew")}</Link>
              </Button>
            </div>
          </div>
        </>
      ) : (
        <form
          className="mt-8 flex flex-col gap-5 rounded-2xl bg-surface-container-low p-6 outline outline-1 outline-outline-variant/15"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError(null);
            patchMutation.mutate();
          }}
        >
          <div>
            <label htmlFor="ed-title" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.titleLabel")}
            </label>
            <input
              id="ed-title"
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              required
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="ed-amount" className="text-label-md text-on-surface-variant">
                {t("dashboardUi.expenses.form.amountLabel")}
              </label>
              <input
                id="ed-amount"
                value={amountInput}
                onChange={(ev) => setAmountInput(ev.target.value)}
                inputMode="decimal"
                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-body text-body-md text-on-surface outline-none focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="ed-ccy" className="text-label-md text-on-surface-variant">
                {t("dashboardUi.expenses.form.currencyLabel")}
              </label>
              <input
                id="ed-ccy"
                value={currency}
                onChange={(ev) => setCurrency(ev.target.value.toUpperCase().slice(0, 3))}
                maxLength={3}
                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 font-mono uppercase text-on-surface outline-none focus:border-primary"
              />
            </div>
          </div>
          <div>
            <label htmlFor="ed-date" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.dateLabel")}
            </label>
            <input
              id="ed-date"
              type="date"
              value={occurredOn}
              onChange={(ev) => setOccurredOn(ev.target.value)}
              required
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 text-on-surface outline-none focus:border-primary"
            />
          </div>
          <div>
            <label htmlFor="ed-cat" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.categoryLabel")}
            </label>
            <select
              id="ed-cat"
              value={categoryId}
              onChange={(ev) => setCategoryId(ev.target.value)}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 text-on-surface outline-none focus:border-primary"
            >
              <option value="">{t("dashboardUi.expenses.form.categoryNone")}</option>
              {(categoriesQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ed-payer" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.paidByLabel")}
            </label>
            <select
              id="ed-payer"
              value={paidBySelect}
              onChange={(ev) => setPaidBySelect(ev.target.value)}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 text-on-surface outline-none focus:border-primary"
            >
              <option value="">{t("dashboardUi.expenses.form.paidByDefault")}</option>
              <option value={PAID_BY_JOINT_SELECT_VALUE}>
                {t("dashboardUi.expenses.form.paidByJointOption")}
              </option>
              {(membersQuery.data ?? []).map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.user.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-label-md text-on-surface-variant">{t("dashboardUi.expenses.form.paidByHint")}</p>
          </div>
          <div>
            <label htmlFor="ed-pay-method" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.paymentMethodLabel")}
            </label>
            <select
              id="ed-pay-method"
              value={paymentMethodDraft}
              onChange={(ev) => setPaymentMethodDraft(ev.target.value as "" | ExpensePaymentMethod)}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 text-on-surface outline-none focus:border-primary"
            >
              <option value="">{t("dashboardUi.expenses.form.paymentMethodNone")}</option>
              <option value="card">{t("dashboardUi.expenses.form.paymentMethodCard")}</option>
              <option value="cash">{t("dashboardUi.expenses.form.paymentMethodCash")}</option>
              <option value="account">{t("dashboardUi.expenses.form.paymentMethodAccount")}</option>
            </select>
          </div>
          <div>
            <label htmlFor="ed-notes" className="text-label-md text-on-surface-variant">
              {t("dashboardUi.expenses.form.notesLabel")}
            </label>
            <textarea
              id="ed-notes"
              value={notes}
              onChange={(ev) => setNotes(ev.target.value)}
              rows={3}
              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface px-4 py-3 text-on-surface outline-none focus:border-primary"
            />
          </div>
          <div>
            <p className="text-label-md text-on-surface-variant">{t("dashboardUi.expenses.detail.receiptLabel")}</p>
            <p className="mt-1 text-body-sm text-on-surface-variant">
              {t("dashboardUi.expenses.detail.receiptReplaceHelp")}
            </p>
            {receiptStorageKey ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => {
                  void openStorageDocumentInNewTab(locale, receiptStorageKey);
                }}
              >
                {t("dashboardUi.expenses.detail.receiptView")}
              </Button>
            ) : null}
            <div className="mt-3">
              <FilePondStorageUpload
                locale={locale}
                mode="vault"
                maxFiles={1}
                inputId="expense-detail-receipt-upload"
                disabled={patchMutation.isPending}
                onStoredKeys={(keys) => {
                  const first = keys[0];
                  if (first) setReceiptStorageKey(first);
                }}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={patchMutation.isPending}>
              {patchMutation.isPending ? t("dashboardUi.expenses.form.saving") : t("dashboardUi.expenses.form.submitSave")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEditing(false);
                setFormError(null);
              }}
            >
              {t("dashboardUi.expenses.form.cancelEdit")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
